import React, { useCallback, useState } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { PreviewPanel } from '@atlassian/preview-panel/preview-panel';
import { PreviewPanelBody } from '@atlassian/preview-panel/preview-panel-body';
import { PreviewPanelHeader } from '@atlassian/preview-panel/preview-panel-header';
import { usePreviewPanelsActions } from '@atlassian/preview-panels-api/use-preview-panel-actions';

import { withSmartCardProviderContext } from '@confluence/editor-features/entry-points/withSmartCardProviderContext.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { TwgTraceIdAnalyticsWrapper } from '../TwgTraceIdAnalyticsWrapper.tsx';

import { PANEL_TYPE, PARENT_PRODUCT } from './previewPanelConstants.ts';
import { useConnectionSectionsData } from './sections/useConnectionSectionsData.ts';
import { TeamworkGraphPreviewPanelContextColumn } from './TeamworkGraphPreviewPanelContextColumn.tsx';
import { TeamworkGraphPreviewPanelFooter } from './TeamworkGraphPreviewPanelFooter.tsx';
import { TeamworkGraphPreviewPanelInfoButton } from './TeamworkGraphPreviewPanelInfoButton.tsx';
import { TeamworkGraphPreviewPanelMoreActions } from './TeamworkGraphPreviewPanelMoreActions.tsx';
import { TeamworkGraphPreviewPanelOpenGraphButton } from './TeamworkGraphPreviewPanelOpenGraphButton.tsx';
import { TeamworkGraphPreviewPanelTitle } from './TeamworkGraphPreviewPanelTitle.tsx';
import { useTeamworkGraphPreviewPanelMode } from './useTeamworkGraphPreviewPanelMode.ts';
import { TeamworkGraphChatColumn } from './visualization/TeamworkGraphChatColumn.tsx';

const styles = cssMap({
	// Keep scrolling inside the body so connections cannot cover the footer.
	scroll: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		minHeight: '0px',
		overflowY: 'auto',
	},
	body: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		minHeight: '0px',
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
	},
	// Rovo chat brings its own padding and needs to reach the panel's edges, so this drops
	// the body's insets. `minHeight: 0` is the bounded-height link the chat needs.
	chatBody: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		minHeight: '0px',
	},
});

export type TeamworkGraphPreviewPanelProps = {
	contentId?: string;
};

export function TeamworkGraphPreviewPanelInner({
	contentId,
}: TeamworkGraphPreviewPanelProps): React.JSX.Element {
	const { close } = usePreviewPanelsActions();
	// The panel is only rendered while it is open, so its sections fetch as soon as it
	// mounts — no `enabled` gate, unlike the modal.
	const connections = useConnectionSectionsData(contentId);
	const { isRovoLLMEnabled } = useSessionData();
	const { isRovo, prefilledPrompt, showRovo, showConnections } = useTeamworkGraphPreviewPanelMode({
		isRovoEnabled: isRovoLLMEnabled,
		contentId,
	});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const openModal = useCallback(() => setIsModalOpen(true), []);
	const closeModal = useCallback(() => setIsModalOpen(false), []);
	// Close first: Rovo unmounts the column that mounts the modal, so an open flag left behind
	// would pop the modal back up on the way back to connections.
	const showRovoFromPanel = useCallback(
		(...args: Parameters<typeof showRovo>) => {
			setIsModalOpen(false);
			showRovo(...args);
		},
		[showRovo],
	);
	// Bound here, not in the footer, so the trigger stays a wiring-point concern like the others.
	const showRovoFromShareContext = useCallback(
		(prompt: string) => showRovoFromPanel('shareContext', prompt),
		[showRovoFromPanel],
	);

	return (
		<PreviewPanel testId="teamwork-graph-preview-panel">
			<PreviewPanelHeader
				onClose={() => close()}
				analyticsClient={null}
				parentProduct={PARENT_PRODUCT}
				panelType={PANEL_TYPE}
				// Dropped straight into the header's action flex row, so a fragment lays the info
				// button, the open-graph button and the "more" dropdown out in a row rather than
				// nesting any of them inside the dropdown.
				moreActions={
					<>
						<TeamworkGraphPreviewPanelInfoButton />
						{!isRovo && <TeamworkGraphPreviewPanelOpenGraphButton onClick={openModal} />}
						<TeamworkGraphPreviewPanelMoreActions />
					</>
				}
			>
				<TeamworkGraphPreviewPanelTitle />
			</PreviewPanelHeader>
			<Box xcss={styles.scroll} testId="teamwork-graph-preview-panel-scroll">
				<PreviewPanelBody
					analyticsClient={null}
					parentProduct={PARENT_PRODUCT}
					panelType={PANEL_TYPE}
					styles={isRovo ? styles.chatBody : styles.body}
				>
					{isRovo ? (
						<TeamworkGraphChatColumn onBack={showConnections} prefilledPrompt={prefilledPrompt} />
					) : (
						<TeamworkGraphPreviewPanelContextColumn
							contentId={contentId}
							connections={connections}
							isRovoEnabled={isRovoLLMEnabled}
							onAskRovo={showRovoFromPanel}
							isModalOpen={isModalOpen}
							onCloseModal={closeModal}
						/>
					)}
				</PreviewPanelBody>
			</Box>
			{/* The chat composer replaces the footer in chat mode. */}
			{!isRovo && (
				<TeamworkGraphPreviewPanelFooter
					contentId={contentId}
					testId="teamwork-graph-preview-panel-footer"
					onAskRovo={isRovoLLMEnabled ? showRovoFromShareContext : undefined}
				/>
			)}
		</PreviewPanel>
	);
}

// `PreviewPanelContainer` renders the panel from `PageLayout`, outside the
// byline's own SmartCard provider, so the panel needs its own — the connection
// rows call `useSmartLinkActions` / `useSmartLinkReload`, which throw without one.
// Being its own tree, it is outside the byline's trace-id wrapper and needs one of its own.
export const TeamworkGraphPreviewPanel: (
	props: TeamworkGraphPreviewPanelProps,
) => React.JSX.Element = withSmartCardProviderContext((props: TeamworkGraphPreviewPanelProps) => (
	<TwgTraceIdAnalyticsWrapper>
		<TeamworkGraphPreviewPanelInner {...props} />
	</TwgTraceIdAnalyticsWrapper>
));
